'use client';

import Box from '@mui/material/Box';
import {styled, type Theme} from '@mui/material/styles';
import NextImage from 'next/image';
import type {FC} from 'react';
import {Typography} from '../primitives';

// Figma "Banner card" (country detail, nodes 2254:65296 desktop / 124:29381
// mobile): a full-bleed country photo under a 50% neutral-dark scrim with the
// commercial name centred over it. 350px tall on desktop, 160px on mobile.
const BANNER_HEIGHT_MOBILE = 160;
const BANNER_HEIGHT_DESKTOP = 350;

export interface BannerCardProps {
  title: string;
  imageSrc: string;
  imageAlt?: string;
  scrim?: boolean;
}

const Root = styled('section')(({theme}) => ({
  position: 'relative',
  width: '100%',
  height: BANNER_HEIGHT_MOBILE,
  overflow: 'hidden',
  backgroundColor: theme.palette.colors.surface.neutral.subtle,
  [theme.breakpoints.up('md')]: {
    height: BANNER_HEIGHT_DESKTOP,
  },
})) as FC<React.HTMLAttributes<HTMLElement>>;

// Full-bleed layer that centres the name; optionally darkens the photo.
const Overlay = styled(Box, {shouldForwardProp: propName => propName !== 'scrim'})<{scrim: boolean}>(({theme, scrim}) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  ...(scrim && {backgroundColor: theme.palette.colors.surface.darkOverlay}),
})) as FC<{scrim: boolean; children?: React.ReactNode}>;

const FULL_BLEED_SIZES = '100vw';

export function BannerCard({title, imageSrc, imageAlt, scrim = true}: BannerCardProps) {
  return (
    <Root>
      {imageSrc && <NextImage src={imageSrc} alt={imageAlt ?? ''} fill sizes={FULL_BLEED_SIZES} style={{objectFit: 'cover'}} priority />}
      <Overlay scrim={scrim}>
        <Typography
          size="headline-1"
          weight="bold"
          mobileSize="title-2"
          mobileWeight="bold"
          as="h1"
          sx={(theme: Theme) => ({
            textTransform: {xs: 'capitalize', md: 'uppercase'},
            color: theme.palette.colors.text.neutral.inverse,
            textAlign: 'center',
          })}
        >
          {title}
        </Typography>
      </Overlay>
    </Root>
  );
}

BannerCard.displayName = 'BannerCard';
